import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { CalendarDays, PackageSearch, Truck } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate, formatDateTime } from '@/lib/date'
import type { TrackResult } from '@/types'
import Button from '@/components/ui/Button'
import LogoLoader from '@/components/ui/LogoLoader'
import StageStrip from '@/components/order/ProductionTimeline'
import ShipmentStatusBadge from '@/components/order/ShipmentStatusBadge'
import ShipmentTimeline from '@/components/order/ShipmentTimeline'

/** Delivery methods are stored in capitals ("SELF PICKUP", "POS LAJU"); short codes like J&T stay as they are. */
function deliveryLabel(method: string): string {
  if (method.toUpperCase() === 'SELF PICKUP') return 'Self pickup'
  const name = method
    .split(' ')
    .map((word) => (word.length > 3 ? word[0].toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ')
  return `Courier - ${name}`
}

const MOVING = ['booked', 'picked_up', 'in_transit', 'out_for_delivery']

/**
 * The one sentence a customer opens this page for. Shipment beats the job
 * once a parcel exists; production states are said in plain words rather
 * than the floor's queued / in_progress.
 */
function statusLine(data: TrackResult): { title: string; detail?: string } {
  if (data.order_status === 'cancelled') return { title: 'Order cancelled', detail: 'Production has stopped.' }
  if (data.shipment?.status === 'delivered') return { title: 'Delivered' }
  if (data.shipment && MOVING.includes(data.shipment.status)) {
    return { title: 'On its way', detail: data.carrier ? `With ${data.carrier}` : undefined }
  }
  if (data.status === 'completed') return { title: 'Production complete' }
  // Queued also means "between sections": finishing one moves the job to the
  // next as queued until that section starts it. Only nothing done yet is a
  // job that has not started.
  if (data.status === 'queued') {
    if (!data.stages.some((stage) => stage.state === 'done')) return { title: 'Waiting to start production' }
    return {
      title: 'In production',
      detail: data.current_department ? `${data.current_department} starts next` : undefined,
    }
  }
  return {
    title: 'In production',
    detail: data.current_department ? `Now at ${data.current_department}` : undefined,
  }
}

/**
 * Public track & trace - view-only, reached only through a shared link. The
 * token in the URL is the job's random share token, copied by a signed-in
 * customer from their order page (or by admin). There is deliberately no
 * landing page or code-entry form: job codes and order numbers are
 * sequential, so a public lookup by code would let anyone browse the order
 * book.
 */
export default function Track() {
  const { code: token } = useParams()

  const { data, isLoading, isError } = useQuery({
    queryKey: ['track', token],
    queryFn: async () =>
      (await api.get<{ data: TrackResult }>(`/storefront/track/${encodeURIComponent(token!)}`)).data.data,
    enabled: Boolean(token),
    retry: false,
  })
  const status = data ? statusLine(data) : null

  return (
    <div className="pb-16 pt-6">
      <div className="mx-auto w-full max-w-3xl px-5">
        <h1 className="text-3xl font-semibold">Track &amp; Trace</h1>
        <p className="mt-1 text-sm text-foreground/60">
          Follow an order through production with the tracking link shared with you
        </p>

        {isLoading && <div className="mt-6"><LogoLoader /></div>}

        {isError && (
          <div className="mt-6 rounded-2xl border border-line bg-foreground/5 p-6 text-center">
            <PackageSearch className="mx-auto size-8 text-foreground/40" />
            <p className="mt-2 font-semibold">This tracking link is not valid</p>
            <p className="mt-1 text-sm text-foreground/60">
              Check that the whole link was copied, or ask for a fresh one. Signed-in customers can
              always see their orders under My Orders.
            </p>
          </div>
        )}

        {data && status && (
          <div className="mt-6 space-y-4">
            <div className="rounded-2xl border border-line bg-white p-5">
              {/* The order number is the one customers know from My Orders and
                  the invoice; the job code is the floor's, so it sits below.
                  No Urgent pill here (client 2026-10-31): the ready-by date
                  already carries the rush, the order page and invoice show the
                  charge, and a shared link should not show a third party a red
                  URGENT. */}
              <div>
                <p className="text-lg font-bold">Order {data.order_number}</p>
                <p className="font-mono text-xs text-foreground/60">Job {data.job_code}</p>
              </div>

              <div className="mt-4 rounded-xl bg-foreground/5 p-4">
                <p className="text-lg font-semibold">{status.title}</p>
                {status.detail && <p className="text-sm text-foreground/70">{status.detail}</p>}
                <div className="mt-3 grid gap-1.5 text-sm sm:flex sm:gap-6">
                  {data.expected_completion_date && (
                    <p className="flex items-center gap-2 text-foreground/70">
                      <CalendarDays className="size-4 shrink-0 text-primary" />
                      Ready by <span className="font-semibold text-foreground">{formatDate(data.expected_completion_date)}</span>
                    </p>
                  )}
                  {data.delivery_method && (
                    <p className="flex items-center gap-2 text-foreground/70">
                      <Truck className="size-4 shrink-0 text-primary" />
                      <span className="font-semibold text-foreground">{deliveryLabel(data.delivery_method)}</span>
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <StageStrip
                  rows={data.stages.map((stage, i) => ({
                    key: i,
                    department: stage.department,
                    date: stage.state === 'done' ? stage.completed_at : stage.target_date,
                    state: stage.state,
                    // A queued job has nothing in progress; mark where it starts.
                    next: data.status === 'queued' && stage.state === 'pending' && stage.department === data.current_department,
                  }))}
                  etd={data.expected_completion_date}
                />
              </div>

              {data.shipment && (
                <div className="mt-5 border-t border-line pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-semibold">Delivery</p>
                    <ShipmentStatusBadge status={data.shipment.status} variant="storefront" />
                  </div>
                  <p className="mt-1 break-all font-mono text-sm text-foreground/70">
                    {data.carrier ? `${data.carrier} ` : ''}
                    {data.shipment.tracking_number}
                  </p>
                  {data.shipment.delivered_at && (
                    <p className="mt-2 text-sm font-semibold text-success">Delivered on {formatDateTime(data.shipment.delivered_at)}</p>
                  )}
                  <ShipmentTimeline
                    className="mt-3"
                    events={data.shipment.events}
                    status={data.shipment.status}
                    variant="storefront"
                  />
                </div>
              )}

              {data.tracking_url && (
                <a href={data.tracking_url} target="_blank" rel="noreferrer" className="mt-4 block">
                  {/* Button is whitespace-nowrap; a long carrier and number overflowed 375px. The
                      number already shows in the Delivery section when there is one. */}
                  <Button variant="outline" className="h-auto w-full whitespace-normal py-2">
                    <Truck className="size-4 shrink-0" /> Track parcel{data.carrier ? ` with ${data.carrier}` : ''}
                    {data.tracking_number && !data.shipment ? ` (${data.tracking_number})` : ''}
                  </Button>
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
